/**
 * Face engine — model loading and descriptor extraction, in the browser.
 * @file src/web/features/face/lib/faceEngine.ts
 *
 * @llm-rule WHEN: Enrolling a face or capturing one for a clock-in
 * @llm-rule AVOID: Uploading a frame on a normal clock-in — those send the descriptor only
 * @llm-rule NOTE: Models are served from /models (copied out of the npm package at
 *   setup, ~6.7 MB). No CDN, so the app works on a locked-down network.
 *
 * Privacy, precisely: a descriptor is a one-way embedding and cannot be turned
 * back into a photograph. ENROLMENT is the one exception — it also uploads a
 * cropped JPEG, kept as the reference image every later clock-in is measured
 * against, and the payload a cloud matcher will be handed. Clock-in and
 * clock-out never send an image.
 */

import * as faceapi from '@vladmandic/face-api';

const MODEL_URL = '/models';

/** Detector input size. 416 balances accuracy against per-frame cost on laptops. */
const DETECTOR_OPTIONS = new faceapi.TinyFaceDetectorOptions({
  inputSize: 416,
  scoreThreshold: 0.5,
});

let loadPromise: Promise<void> | null = null;

/**
 * Load the three models once per page. Concurrent callers share the same
 * promise, so mounting two capture components doesn't double the download.
 */
export function loadModels(): Promise<void> {
  if (!loadPromise) {
    loadPromise = (async () => {
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
    })().catch((err) => {
      // Let the next attempt retry rather than caching the failure forever.
      loadPromise = null;
      throw err;
    });
  }
  return loadPromise;
}

export function modelsReady(): boolean {
  return (
    faceapi.nets.tinyFaceDetector.isLoaded &&
    faceapi.nets.faceLandmark68Net.isLoaded &&
    faceapi.nets.faceRecognitionNet.isLoaded
  );
}

export interface CaptureResult {
  descriptor: number[];
  /** Detector confidence for the chosen face, 0–1. */
  confidence: number;
  /**
   * Base64 JPEG of the face, cropped from the frame. Only produced when
   * explicitly asked for — the enrolment reference photo. Day-to-day clock-ins
   * do not carry an image.
   */
  image?: string;
}

export type CaptureFailure =
  | 'NO_FACE'
  | 'MULTIPLE_FACES'
  | 'TOO_SMALL'
  | 'MODELS_NOT_READY';

export class FaceCaptureError extends Error {
  constructor(public readonly kind: CaptureFailure, message: string) {
    super(message);
    this.name = 'FaceCaptureError';
  }
}

/** Human-readable coaching for each failure, shown under the camera. */
export const CAPTURE_HINT: Record<CaptureFailure, string> = {
  NO_FACE: 'No face detected — move into the frame and make sure the light is on you',
  MULTIPLE_FACES: 'More than one face in frame — only you should be visible',
  TOO_SMALL: 'Move closer so your face fills the oval',
  MODELS_NOT_READY: 'Still loading the face models — try again in a moment',
};

/**
 * Extract a descriptor from a live video element.
 *
 * Rejects multi-face frames outright: allowing them would let someone hold up
 * a colleague's photo next to their own face and have the matcher pick the
 * one that passes.
 */
export async function captureDescriptor(
  video: HTMLVideoElement,
  options: { withImage?: boolean } = {},
): Promise<CaptureResult> {
  if (!modelsReady()) {
    throw new FaceCaptureError('MODELS_NOT_READY', CAPTURE_HINT.MODELS_NOT_READY);
  }

  const detections = await faceapi
    .detectAllFaces(video, DETECTOR_OPTIONS)
    .withFaceLandmarks()
    .withFaceDescriptors();

  if (!detections.length) {
    throw new FaceCaptureError('NO_FACE', CAPTURE_HINT.NO_FACE);
  }

  if (detections.length > 1) {
    throw new FaceCaptureError('MULTIPLE_FACES', CAPTURE_HINT.MULTIPLE_FACES);
  }

  const [face] = detections;

  // A face smaller than ~18% of the frame height is too low-resolution for a
  // trustworthy embedding — that's the "move closer" case, not a match failure.
  const frameHeight = video.videoHeight || video.clientHeight || 1;
  if (face.detection.box.height / frameHeight < 0.18) {
    throw new FaceCaptureError('TOO_SMALL', CAPTURE_HINT.TOO_SMALL);
  }

  return {
    descriptor: Array.from(face.descriptor),
    confidence: face.detection.score,
    ...(options.withImage ? { image: cropFace(video, face.detection.box) } : {}),
  };
}

/**
 * Cut the face out of the current video frame as a base64 JPEG.
 *
 * Padded by 40% around the detector's box, because the detector crops tight to
 * the features and a reference photo with no forehead or chin is hard for a
 * human — or a cloud matcher — to check against later. Square, so it displays
 * as an avatar without being squashed.
 */
function cropFace(
  video: HTMLVideoElement,
  box: { x: number; y: number; width: number; height: number },
): string {
  const PAD = 0.4;
  const OUT = 320;

  const side = Math.max(box.width, box.height) * (1 + PAD);
  const cx = box.x + box.width / 2;
  const cy = box.y + box.height / 2;

  // Keep the crop inside the frame, or the canvas draws transparent edges.
  const sx = Math.max(0, Math.min(cx - side / 2, video.videoWidth - side));
  const sy = Math.max(0, Math.min(cy - side / 2, video.videoHeight - side));
  const sSide = Math.min(side, video.videoWidth, video.videoHeight);

  const canvas = document.createElement('canvas');
  canvas.width = OUT;
  canvas.height = OUT;

  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  ctx.drawImage(video, sx, sy, sSide, sSide, 0, 0, OUT, OUT);

  // 0.82 keeps a recognisable face around 20–30 KB, small enough to store on
  // the row and send to a matcher without a blob store.
  return canvas.toDataURL('image/jpeg', 0.82);
}

/**
 * Average several captures into one enrolment descriptor.
 *
 * Enrolling from a single frame bakes in that frame's lighting and angle.
 * Averaging a handful of frames produces a centroid that matches far more
 * reliably day to day.
 */
export function averageDescriptors(samples: number[][]): number[] {
  if (!samples.length) throw new Error('No samples to average');

  const length = samples[0].length;
  const out = new Array<number>(length).fill(0);

  for (const sample of samples) {
    for (let i = 0; i < length; i++) out[i] += sample[i];
  }

  return out.map((v) => v / samples.length);
}
